// Design token layer.
//
// Two-tier system:
//
//   1. PRIMITIVES (this file, as TS constants — NOT in the CSS output).
//      Raw palette, spacing scale, radii, shadows, etc. These are the
//      vocabulary from which semantic tokens are composed. Components
//      MUST NOT import from `palette` / `space` / `radius` directly —
//      they're build-time values used only here to construct the
//      semantic theme below.
//
//   2. SEMANTIC TOKENS (exported `vars` object, emitted as CSS custom
//      properties at :root). This is the only layer components are
//      allowed to reference. Adding a new style means either (a) using
//      an existing semantic token, or (b) adding a NEW semantic token
//      — a deliberate act that requires thinking about "what role does
//      this color play in the design system" rather than "I want this
//      specific hex value here."
//
// The guardrail is social, not mechanical: nothing stops a developer
// from importing `palette` directly if they really want to. But every
// code review should push back on any such import outside this file.
//
// Light theme only for now. Dark / high-contrast themes will land via
// vanilla-extract's createTheme when needed — the semantic tokens are
// already positioned for that switch (components don't know which
// theme is active; they just read the vars).
//
// Source for Teresa's design cues: docs/teresa-prototype-findings.md
// — dark sidebar (navy #1B2A4A), gold accent, Barlow Condensed for
// headings, Nunito Sans for body.

import { createGlobalTheme } from "@vanilla-extract/css";

// ── Primitives ─────────────────────────────────────────────────────────

// Navy — the brand primary. Teresa's exact sidebar shade lives at navy700.
const palette = {
  // Navy (brand primary)
  navy50: "#eef1f8",
  navy100: "#d4dcec",
  navy200: "#a8b8d8",
  navy300: "#7a92c0",
  navy400: "#4f70a5",
  navy500: "#2d4e87",
  navy600: "#1e3865",
  navy700: "#1B2A4A", // Teresa's sidebar
  navy800: "#131f37",
  navy900: "#0a1323",

  // Gold — ceremony only, surfaced as the `gold` / `goldSoft` semantic
  // tokens below. NOT the sidebar accent: that switched to olive
  // because gold on navy read as too decorative for an operational
  // tool. Gold marks the rare celebratory number, nothing functional.
  gold300: "#e8c770",
  gold400: "#d9b04c",
  gold500: "#c79828",
  gold600: "#a67d14",

  // Olive — brand accent for the sidebar. Intentionally separate
  // from the status-green palette below: olive is "this is the
  // firm's secondary brand color," status-green is "something
  // completed / OK." Using one for both would muddle the semantics.
  olive400: "#89a94a",
  olive500: "#6b8e23",
  olive600: "#556f1c",

  // Neutrals — pure grays, used for text / surfaces / borders
  white: "#ffffff",
  gray50: "#f8f9fa",
  gray100: "#f0f2f5",
  gray200: "#e5e7eb",
  gray300: "#d1d5db",
  gray400: "#9ca3af",
  gray500: "#6b7280",
  gray600: "#4b5563",
  gray700: "#374151",
  gray800: "#1f2937",
  gray900: "#111827",
  black: "#000000",

  // Status colors — each has a 50 (surface tint), 200 (tint border),
  // 500/600 (solid), and 700 (on-tint text) so we can build both
  // filled buttons and tinted badges from the same family.
  red50: "#fef2f2",
  red200: "#fecaca",
  red500: "#ef4444",
  red600: "#dc2626",
  red700: "#b91c1c",

  green50: "#f0fdf4",
  green200: "#bbf7d0",
  green500: "#22c55e",
  green600: "#16a34a",
  green700: "#15803d",

  amber50: "#fffbeb",
  amber200: "#fde68a",
  amber500: "#f59e0b",
  amber600: "#d97706",
  amber700: "#b45309",

  blue50: "#eff6ff",
  blue200: "#bfdbfe",
  blue500: "#3b82f6",
  blue600: "#2563eb",
  blue700: "#1d4ed8",
} as const;

// Spacing scale — powers of 4 (rem). Named by step, not semantic size,
// so the semantic layer can remap them freely.
const space = {
  0: "0",
  // Half-step. Earns its place as the hairline gap between two lines of
  // the same label (name over meta, value over caption) where 4px already
  // reads as a separation rather than a pairing.
  0.5: "0.125rem", // 2px
  1: "0.25rem", //  4px
  2: "0.5rem", //   8px
  3: "0.75rem", // 12px
  4: "1rem", //    16px
  5: "1.5rem", //  24px
  6: "2rem", //    32px
  7: "3rem", //    48px
  8: "4rem", //    64px
  9: "6rem", //    96px
} as const;

// Border radii
const radius = {
  none: "0",
  sm: "2px",
  md: "4px",
  lg: "8px",
  xl: "12px",
  full: "9999px", // pills / circles
} as const;

// Shadows — minimal set, elevation-driven
const shadow = {
  none: "none",
  sm: "0 1px 2px rgba(0, 0, 0, 0.05)",
  md: "0 2px 4px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)",
  lg: "0 8px 16px rgba(0, 0, 0, 0.10), 0 2px 4px rgba(0, 0, 0, 0.06)",
  // Focus ring — rendered as a box-shadow so it doesn't affect layout
  // the way an outline or border would.
  focus: `0 0 0 3px ${palette.navy400}55`,
} as const;

// Font families — stack includes system fallbacks so the UI stays
// readable while the webfonts are still loading (font-display: swap on
// the @font-face declarations).
//
// THE METRICS RULE: Barlow Condensed is a DISPLAY face and only legible
// as one — at h3/h4 sizes its condensed counters close up and it reads
// as cramped. So it is bound to `display` and `display` alone, which
// callers may use at 20px and above. `heading` is Nunito Sans, same as
// `body`: below 20px the two-family pairing is what looked wrong, not
// Barlow itself.
const fontFamily = {
  display: '"Barlow Condensed", system-ui, -apple-system, "Segoe UI", sans-serif',
  body: '"Nunito Sans", system-ui, -apple-system, "Segoe UI", sans-serif',
  heading: '"Nunito Sans", system-ui, -apple-system, "Segoe UI", sans-serif',
  mono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
} as const;

// Font sizes — rem-based, 1rem = 16px by default
const fontSize = {
  xs: "0.75rem", //  12px — fine print, badges
  sm: "0.875rem", // 14px — table cells, dense UI
  md: "1rem", //     16px — body default
  lg: "1.125rem", // 18px — emphasized body
  xl: "1.25rem", //  20px — subheadings
  "2xl": "1.5rem", //    24px — section headings
  "3xl": "1.875rem", //  30px — page headings
  "4xl": "2.25rem", //   36px — display
} as const;

const fontWeight = {
  regular: "400",
  medium: "500",
  semibold: "600",
  bold: "700",
} as const;

const lineHeight = {
  tight: "1.2",
  normal: "1.5",
  relaxed: "1.75",
} as const;

// Z-index scale — a fixed ladder keeps stacking-context surprises
// contained. Components should prefer the named slots over arbitrary
// integers.
// Motion — three durations and one curve. Nothing bounces: this is an
// operational tool, so motion exists to say "the thing you asked for
// happened", never to entertain. `fast` is control feedback (hover,
// focus), `base` is an element arriving or leaving, `stage` is the one
// theatrical moment (a whole panel reconfiguring). Every one of these
// collapses to instant under prefers-reduced-motion (styles/reduced-
// motion.css).
const motion = {
  fast: "120ms",
  base: "200ms",
  stage: "250ms",
  easing: "cubic-bezier(0.2, 0, 0, 1)",
} as const;

const zIndex = {
  base: "0",
  dropdown: "10",
  sticky: "20",
  overlay: "30",
  modal: "40",
  toast: "50",
} as const;

// ── Semantic tokens (the CSS theme) ────────────────────────────────────
//
// Components reference these via the exported `vars` object:
//
//   import { vars } from "~/styles/tokens.css";
//   style({
//     backgroundColor: vars.color.surface,
//     color: vars.color.textDefault,
//     padding: vars.space.md,
//   });
//
// vanilla-extract generates a CSS custom property for each leaf and
// emits them at :root. The `vars` object is a type-safe accessor that
// returns the generated var reference strings (e.g. "var(--color-surface__abc)").

export const vars = createGlobalTheme(":root", {
  color: {
    // Page / surface backgrounds
    background: palette.gray50,
    surface: palette.white,
    surfaceMuted: palette.gray100,

    // Text
    textDefault: palette.gray900,
    textMuted: palette.gray500,
    textSubtle: palette.gray400,
    textInverse: palette.white,

    // Borders
    borderSubtle: palette.gray200,
    borderStrong: palette.gray400,
    focusRing: `${palette.navy400}aa`,

    // Interactive — primary actions
    primary: palette.navy700,
    primaryHover: palette.navy600,
    primaryActive: palette.navy800,
    primaryText: palette.white,

    // Interactive — secondary (outline/ghost buttons)
    secondary: palette.white,
    secondaryHover: palette.gray100,
    secondaryActive: palette.gray200,
    secondaryText: palette.gray900,

    // Status — solid (buttons, strong fills)
    danger: palette.red600,
    dangerHover: palette.red700,
    dangerText: palette.white,
    success: palette.green600,
    warning: palette.amber500,
    info: palette.blue600,

    // Status — tinted surfaces (badges, inline alerts). Pair surface
    // + border + onSurface text so callers can render a legible
    // chip or banner without mixing colors at render time.
    dangerSurface: palette.red50,
    dangerSurfaceBorder: palette.red200,
    dangerOnSurface: palette.red700,

    successSurface: palette.green50,
    successSurfaceBorder: palette.green200,
    successOnSurface: palette.green700,

    warningSurface: palette.amber50,
    warningSurfaceBorder: palette.amber200,
    warningOnSurface: palette.amber700,

    infoSurface: palette.blue50,
    infoSurfaceBorder: palette.blue200,
    infoOnSurface: palette.blue700,

    // Neutral tint — for "no specific status" badges (e.g. counts)
    neutralSurface: palette.gray100,
    neutralSurfaceBorder: palette.gray200,
    neutralOnSurface: palette.gray700,

    // Brand tints — the same surface/border/on-surface triad as the
    // status families, for chips that name a BRAND position rather than
    // a status (pipeline stages, phases). Navy and olive are the two
    // brand colors, so they get the two extra triads; a chip must never
    // mix families.
    navySurface: palette.navy50,
    navySurfaceBorder: palette.navy200,
    navyOnSurface: palette.navy600,

    oliveSurface: "rgba(107, 142, 35, 0.12)", // olive500 @ 12%
    oliveSurfaceBorder: palette.olive400,
    oliveOnSurface: palette.olive600,

    // The scrim behind a modal surface. One token because there is one
    // modal treatment: anything that dims the page to ask a question dims
    // it by exactly this much.
    overlayScrim: "rgba(17, 24, 39, 0.45)", // gray900 @ 45%

    // Gold — ceremony, never a functional status. The palette above kept
    // the gold family "for future use"; this is that use: the one KPI
    // that earns a crown (signed this week) and nothing else.
    gold: palette.gold500,
    goldSoft: "rgba(199, 152, 40, 0.14)", // gold500 @ 14%

    // Live-status language — dots and edges on things that are happening
    // right now (threads today; any long-lived work item next).
    // Separate from the status families above: those describe an OUTCOME
    // (this succeeded, that's overdue), these describe a LIVENESS. Always
    // paired with a glyph or label, never color alone.
    statusActive: palette.green500,
    statusWaiting: palette.amber500,
    statusScheduled: palette.blue500,
    statusIdle: palette.gray400,
    statusBreach: palette.red600,

    // Timer treatments. Warning is a quiet amber chip; breach is the one
    // solid red fill in the system — earned by being the thing the
    // product exists to prevent.
    timerWarnText: palette.amber700,
    timerWarnSurface: palette.amber50,
    timerBreachText: palette.white,
    timerBreachSurface: palette.red600,

    // Sidebar — dark navy nav with olive active/brand accent.
    // Olive reads as muted + earthy on navy (legal / firm aesthetic),
    // where bright green would read as "success indicator" and gold
    // as decorative. Separate from status-green on purpose.
    sidebarBackground: palette.navy700,
    sidebarSurface: palette.navy800,
    sidebarText: palette.white,
    sidebarTextMuted: palette.navy200,
    sidebarAccent: palette.olive500,
    sidebarAccentText: palette.white,

    // Soft sidebar-accent washes for hover/active backgrounds. Pre-
    // mixed so components don't have to reach for raw rgba values.
    sidebarAccentSoft: "rgba(107, 142, 35, 0.12)", //  olive500 @ 12%
    sidebarAccentSoftHover: "rgba(107, 142, 35, 0.22)", // olive500 @ 22%
  },

  // Spacing — mapped from primitive numeric scale to semantic names.
  // Components use xs/sm/md/lg/xl/2xl; adding a new spacing value
  // requires adding a new semantic token here, not reaching for a
  // raw primitive.
  space: {
    none: space[0],
    "2xs": space[0.5], // 2px
    xs: space[1], //  4px
    sm: space[2], //  8px
    md: space[4], // 16px
    lg: space[5], // 24px
    xl: space[6], // 32px
    "2xl": space[7], // 48px
    "3xl": space[8], // 64px
  },

  radius: {
    none: radius.none,
    sm: radius.sm,
    md: radius.md,
    lg: radius.lg,
    xl: radius.xl,
    full: radius.full,
  },

  shadow: {
    none: shadow.none,
    sm: shadow.sm,
    md: shadow.md,
    lg: shadow.lg,
    focus: shadow.focus,
  },

  font: {
    // Barlow Condensed — 20px and up only (see the metrics rule above).
    display: fontFamily.display,
    body: fontFamily.body,
    heading: fontFamily.heading,
    mono: fontFamily.mono,
  },

  fontSize: {
    xs: fontSize.xs,
    sm: fontSize.sm,
    md: fontSize.md,
    lg: fontSize.lg,
    xl: fontSize.xl,
    "2xl": fontSize["2xl"],
    "3xl": fontSize["3xl"],
    "4xl": fontSize["4xl"],
  },

  fontWeight: {
    regular: fontWeight.regular,
    medium: fontWeight.medium,
    semibold: fontWeight.semibold,
    bold: fontWeight.bold,
  },

  lineHeight: {
    tight: lineHeight.tight,
    normal: lineHeight.normal,
    relaxed: lineHeight.relaxed,
  },

  motion: {
    fast: motion.fast,
    base: motion.base,
    stage: motion.stage,
    easing: motion.easing,
  },

  zIndex: {
    base: zIndex.base,
    dropdown: zIndex.dropdown,
    sticky: zIndex.sticky,
    overlay: zIndex.overlay,
    modal: zIndex.modal,
    toast: zIndex.toast,
  },
});
